import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useExpertProfileFilters } from '@/hooks/useExpertProfileFilters';
import { useExpertProfiles } from '@/hooks/useExpertProfiles';
import ExpertProfilesTable from '@/components/ExpertProfilesTable';
import { Pagination } from '@/components/Pagination';
import type { ExpertProfileFilters } from '@/types/expert-profile';

export default function ExpertProfilesPage() {
  const navigate = useNavigate();
  const { filters, updateFilter, clearFilters, hasActiveFilters } = useExpertProfileFilters();
  const { data, isLoading, isError, error } = useExpertProfiles(filters);

  useEffect(() => {
    document.title = 'Expert Profiles | Admin Dashboard';
    return () => {
      document.title = 'Admin Dashboard';
    };
  }, []);

  useEffect(() => {
    if (isError && error) {
      toast.error(error instanceof Error ? error.message : 'Failed to load expert profiles');
    }
  }, [isError, error]);

  const handleSort = useCallback(
    (column: ExpertProfileFilters['sortBy']) => {
      if (filters.sortBy === column) {
        updateFilter('sortOrder', filters.sortOrder === 'asc' ? 'desc' : 'asc');
      } else {
        updateFilter('sortBy', column);
        updateFilter('sortOrder', 'asc');
      }
    },
    [filters.sortBy, filters.sortOrder, updateFilter]
  );

  const totalPages = data ? Math.ceil(data.total / data.pageSize) : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-pine-display font-display text-pine-ink">Expert Profiles</h1>
            <p className="text-pine-body text-pine-ink-soft mt-1">
              Manage expert profiles for the Expert Tips feature
            </p>
          </div>
          <button
            onClick={() => navigate('/admin/expert-profiles/new')}
            className="admin-button-primary flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            <span>New Expert Profile</span>
          </button>
        </div>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <div className="flex items-center gap-4">
          <select
            value={filters.status}
            onChange={(e) => updateFilter('status', e.target.value as ExpertProfileFilters['status'])}
            className="px-4 py-2 rounded-lg border border-pine-line bg-white text-pine-ink"
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-sm text-pine-ink-soft hover:text-pine-ink underline"
            >
              Clear filters
            </button>
          )}
        </div>
      </motion.div>

      {/* Error State */}
      {isError && (
        <div className="admin-card p-6 border border-red-500/20 bg-red-500/5">
          <p className="text-red-400">Failed to load expert profiles. Please try again.</p>
        </div>
      )}

      {/* Table */}
      {!isError && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <ExpertProfilesTable
            profiles={data?.items || []}
            isLoading={isLoading}
            isEmpty={!isLoading && data?.total === 0 && !hasActiveFilters}
            sortBy={filters.sortBy}
            sortOrder={filters.sortOrder}
            onSort={handleSort}
          />
        </motion.div>
      )}

      {/* Pagination */}
      {!isError && data && totalPages > 1 && (
        <Pagination
          currentPage={filters.page}
          totalPages={totalPages}
          totalItems={data.total}
          pageSize={filters.pageSize}
          onPageChange={(page) => updateFilter('page', page)}
        />
      )}
    </div>
  );
}
